import { useApp } from "../lib/AppContext";
import { Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, AlertTriangle, ListChecks, FolderKanban, LayoutGrid, FileText,
  ArrowRight, Target,
} from "lucide-react";
import { api } from "../lib/api";
import EndOfDayBanner from "../components/EndOfDayBanner";
import MomentumCard from "../components/MomentumCard";
import TodayBuildCard from "../components/TodayBuildCard";
import EisenhowerCards from "../components/EisenhowerCards";
import CicloAccordion from "../components/CicloAccordion";
import TodayAgenda from "../components/TodayAgenda";
import HomeCtaBar from "../components/HomeCtaBar";
import TaskForm from "../components/TaskForm";
import { eisenhowerKeyFromTask } from "../lib/eisenhower";

// Bug 4/13/18/19 · gli eventi puntuali NON sono cicli e non popolano
// né la sezione "Cicli aperti" né le card Eisenhower. Restano solo in Agenda.
const EVENT_TYPES = new Set(["event", "call", "meeting", "appointment"]);
const OPEN_STATUS = new Set(["open", "in_progress", "waiting"]);
const EIS_RANK = { costruisci: 0, gestisci: 1, risolvi: 2 };

const HORIZON_LABEL = { "1_year": "1 anno", "5_years": "5 anni", "10_years": "10 anni" };

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const QUICK_CARDS = [
  { to: "/agenda?view=week", label: "La mia settimana", icon: Calendar, description: "Panoramica dei prossimi 7 giorni", testid: "home-card-agenda-week" },
  { to: "/deadlines", label: "Le mie scadenze", icon: AlertTriangle, description: "Cosa scade prima", testid: "home-card-deadlines" },
  { to: "/tasks", label: "Cicli · tutti", icon: ListChecks, description: "Vista completa dei cicli", testid: "home-card-tasks" },
  { to: "/projects", label: "Progetti", icon: FolderKanban, description: "Contenitori di lavoro", testid: "home-card-projects" },
  { to: "/life-areas", label: "Aree", icon: LayoutGrid, description: "Struttura della tua vita", testid: "home-card-areas" },
  { to: "/documenti", label: "Documenti", icon: FileText, description: "Fatture, ricevute, estratti", testid: "home-card-documents" },
];

export default function Home() {
  const { user } = useApp();
  const [tasks, setTasks] = useState([]);
  const [agendaToday, setAgendaToday] = useState([]);
  const [areas, setAreas] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, ar, ag, gs] = await Promise.all([
        api.listTasks({ include_completed: false }).catch(() => []),
        api.listAreas().catch(() => []),
        api.agenda("day").catch(() => ({ tasks: [] })),
        api.listGoalsWithProgress().catch(() => api.listGoals({ status: "active" }).catch(() => [])),
      ]);
      setTasks(Array.isArray(t) ? t : []);
      setAreas(Array.isArray(ar) ? ar : []);
      setAgendaToday(Array.isArray(ag?.tasks) ? ag.tasks : []);
      const active = (Array.isArray(gs) ? gs : []).filter((g) => g.status === "active");
      active.sort((a, b) => new Date(a.target_date || "9999-01-01") - new Date(b.target_date || "9999-01-01"));
      setGoals(active);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const areasById = useMemo(() => Object.fromEntries((areas || []).map((a) => [a.id, a])), [areas]);
  const firstName = user?.first_name || "utente";
  const todayLabel = new Date().toLocaleDateString("it-IT", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });

  // Enrich tasks with Eisenhower key
  const enrichedTasks = useMemo(
    () => (tasks || []).map((t) => ({ ...t, __eisKey: eisenhowerKeyFromTask(t) })),
    [tasks],
  );

  // ─── Sezione 1 · "Oggi costruisci questo" (max 3) ────────────
  const todayActions = useMemo(() => {
    const iso = todayIso();
    return enrichedTasks
      .filter((t) => t.__eisKey && !EVENT_TYPES.has(t.type)
        && ((t.scheduled_date === iso)
            || (t.due_date && t.due_date <= iso)))
      .sort((a, b) => {
        const r = (EIS_RANK[a.__eisKey] ?? 9) - (EIS_RANK[b.__eisKey] ?? 9);
        if (r !== 0) return r;
        const da = a.due_date || a.scheduled_date || "9999-99-99";
        const db = b.due_date || b.scheduled_date || "9999-99-99";
        return da < db ? -1 : da > db ? 1 : 0;
      })
      .slice(0, 3);
  }, [enrichedTasks]);

  // ─── Sezione 2 · Card Eisenhower (contano cicli, NO eventi/azioni figlie) ────
  const eisGroups = useMemo(() => {
    const g = {
      costruisci: { count: 0, titles: [] },
      gestisci:   { count: 0, titles: [] },
      risolvi:    { count: 0, titles: [] },
    };
    enrichedTasks.forEach((t) => {
      if (EVENT_TYPES.has(t.type)) return;
      if (t.parent_task_id) return;  // Bug 18 · azioni figlie NON contano come cicli
      if (!OPEN_STATUS.has(t.status)) return;
      const k = t.__eisKey;
      if (k && g[k]) {
        g[k].count++;
        if (g[k].titles.length < 5) g[k].titles.push(t.title);
      }
    });
    return g;
  }, [enrichedTasks]);

  // ─── Sezione 3 · Cicli aperti (accordion collapsed) ─────────
  const openCycles = useMemo(
    () => enrichedTasks.filter((t) =>
      !EVENT_TYPES.has(t.type)
      && !t.parent_task_id
      && OPEN_STATUS.has(t.status)
      && (t.scheduled_date || t.due_date)),
    [enrichedTasks],
  );

  // ─── Sezione 4 · Agenda oggi (eventi + azioni schedulate oggi) ─
  const todayAgendaItems = useMemo(() => {
    const iso = todayIso();
    return (agendaToday || [])
      .filter((t) => t.scheduled_date === iso)
      .sort((a, b) => {
        const ta = a.scheduled_time || "99:99";
        const tb = b.scheduled_time || "99:99";
        return ta < tb ? -1 : ta > tb ? 1 : 0;
      });
  }, [agendaToday]);

  const handleCompleteTaskInAgenda = async (id) => {
    try {
      await api.completeTask(id);
      toast.success("✓ Completato");
      load();
    } catch { toast.error("Errore"); }
  };
  // Suppress unused warning in a way React-friendly
  void handleCompleteTaskInAgenda;

  const nearestGoals = goals.slice(0, 3);

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Header */}
      <div>
        <div className="text-xs font-mono uppercase tracking-widest opacity-70">{todayLabel}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-1" data-testid="home-greeting">
          Ciao, {firstName}.
        </h1>
        <p className="mt-2 text-sm opacity-80 max-w-2xl">
          La tua cabina di regia. Focus di oggi, cicli in corso, agenda e azioni rapide.
        </p>
      </div>

      <EndOfDayBanner onResolved={load} />
      <MomentumCard />

      {/* 1 · Hero "Oggi costruisci questo" */}
      <TodayBuildCard
        items={todayActions}
        areasById={areasById}
        onOpenTask={(t) => setEditingTask(t)}
      />

      {/* 2 · Tre card Eisenhower */}
      <EisenhowerCards groups={eisGroups} />

      {/* 3 · Cicli aperti — accordion collapsed di default */}
      <section className="space-y-3" data-testid="home-open-cycles">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Cicli aperti</h2>
          <Link to="/cicli" className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                data-testid="home-open-cycles-see-all">
            Vedi tutti <ArrowRight size={12}/>
          </Link>
        </div>
        {loading ? (
          <div className="pxos-card p-6 text-sm opacity-70" data-testid="home-open-cycles-loading">
            Carico i cicli…
          </div>
        ) : openCycles.length === 0 ? (
          <div className="pxos-card p-6 text-sm opacity-70" data-testid="home-open-cycles-empty">
            Non hai ancora cicli aperti in agenda. Aggiungi un ciclo dalla barra sotto.
          </div>
        ) : (
          <div className="space-y-2">
            {openCycles.slice(0, 5).map((c) => (
              <CicloAccordion
                key={c.id}
                cycle={c}
                area={c.area_id ? areasById[c.area_id] : null}
                onChanged={load}
              />
            ))}
          </div>
        )}
      </section>

      {/* 4 · Agenda oggi */}
      <TodayAgenda items={todayAgendaItems} areasById={areasById} />

      {/* 5 · CTA */}
      <HomeCtaBar onCreated={load} />

      {/* Mete attive — sezione di ancoraggio, invariata rispetto a Bug 17 */}
      {goals.length > 0 && (
        <section className="pxos-card p-4" data-testid="home-goals-active">
          <div className="flex items-center justify-between px-2 pb-2">
            <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
              <Target size={16}/> Mete attive
              <span className="text-[10px] uppercase tracking-widest opacity-60 px-1.5 py-0.5 rounded-full border"
                    data-testid="home-goals-count">
                {goals.length}
              </span>
            </h2>
            <Link to="/mete" className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                  data-testid="home-goals-see-all">
              Vedi tutti <ArrowRight size={12}/>
            </Link>
          </div>
          <ul className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
            {nearestGoals.map((g) => (
              <li key={g.id} className="flex items-center gap-3 p-2" data-testid="home-goal-row">
                <div className="h-8 w-8 rounded-full grid place-items-center bg-black/5 shrink-0">
                  <Target size={14}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2"
                       style={{ color: "var(--pxos-text-dark)" }}>
                    <span className="truncate">{g.title}</span>
                    {typeof g.progress_pct === "number" && (
                      <span className="text-[10px] font-mono opacity-70 shrink-0" data-testid="home-goal-progress">
                        {Math.round(g.progress_pct)}%
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] opacity-60 mt-0.5">
                    {HORIZON_LABEL[g.horizon] || g.horizon}
                    {g.target_date && <> · scadenza {new Date(g.target_date).toLocaleDateString("it-IT")}</>}
                    {g.category && <> · {g.category}</>}
                  </div>
                </div>
                <Link to="/mete" className="text-xs underline opacity-70 shrink-0">Vai</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Quick nav — collegamenti alle altre superfici */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-quick-cards">
        {QUICK_CARDS.map((c) => (
          <Link key={c.to} to={c.to} data-testid={c.testid}
                className="pxos-card p-5 hover:shadow-md transition group">
            <div className="flex items-center gap-2">
              <c.icon size={18} className="opacity-70"/>
              <div className="text-xs uppercase tracking-widest opacity-70">Superficie</div>
            </div>
            <div className="font-display text-lg font-semibold mt-1">{c.label}</div>
            <div className="text-xs opacity-70 mt-1">{c.description}</div>
            <div className="mt-3 text-xs opacity-60 group-hover:opacity-100 inline-flex items-center gap-1">
              Vai <ArrowRight size={12}/>
            </div>
          </Link>
        ))}
      </div>

      {/* Modal edit task (aperto da TodayBuild click) */}
      <TaskForm
        open={!!editingTask} initial={editingTask}
        onClose={() => setEditingTask(null)}
        onSaved={() => { setEditingTask(null); load(); }}
      />
    </div>
  );
}
